import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Alert,
    Chip,
    Menu,
    MenuItem,
    CircularProgress,
    Avatar,
    Tooltip,
    Tab,
    Tabs,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Divider,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    FlightTakeoff as FlightIcon,
    Schedule as ScheduleIcon,
    Place as PlaceIcon,
    Person as PersonIcon,
    Share as ShareIcon,
    Lock as LockIcon,
    Business as BusinessIcon,
    Height as HeightIcon,
    Adjust as RadiusIcon,
    ContentCopy as CopyIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    getMissionTemplates,
    updateMissionTemplate,
    deleteMissionTemplate,
    createMissionTemplate
} from '../../network/apis/missionTemplate.api';
import { useOrganization } from '../../context/OrganizationContext';
import EditTemplateDialog from '../../components/EditTemplateDialog';
import TemplatePreviewDialog from '../../components/TemplatePreviewDialog';

/**
 * Operation Templates Page
 * Displays operation templates with role-based access control
 * - Pilots: See their own templates + shared templates
 * - Owners/Managers: See all templates in organization
 */
const MissionTemplates = () => {
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const { currentOrganization, userRole } = useOrganization();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State management
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [currentTab, setCurrentTab] = useState(0); // 0: All, 1: My Templates, 2: Shared
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [templateToEdit, setTemplateToEdit] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [templateToPreview, setTemplateToPreview] = useState(null);

    // Check user role
    const isOwnerOrManager = userRole && ['owner', 'admin', 'manager'].includes(userRole.toLowerCase());

    // Load templates on mount and when organization changes
    useEffect(() => {
        loadTemplates();
    }, [currentOrganization]);

    // Filter templates based on selected tab
    useEffect(() => {
        filterTemplates();
    }, [templates, currentTab]);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const data = await getMissionTemplates(
                token,
                {},
                currentOrganization?.organizationUuid || null
            );
            setTemplates(data);
            setError(null);
        } catch (err) {
            setError('Failed to load operation templates');
        } finally {
            setLoading(false);
        }
    };

    const filterTemplates = () => {
        let filtered = [...templates];

        switch (currentTab) {
            case 1: // My Templates
                filtered = templates.filter(t => t.createdBy?.userId === user?.sub);
                break;
            case 2: // Shared Templates
                filtered = templates.filter(t => t.isShared === true);
                break;
            default: // All Templates
                break;
        }

        setFilteredTemplates(filtered);
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleEditTemplate = (template) => {
        setTemplateToEdit(template);
        setEditDialogOpen(true);
        handleMenuClose();
    };

    const handleTemplateUpdated = () => {
        setEditDialogOpen(false);
        setTemplateToEdit(null);
        loadTemplates(); // Refresh the list
    };

    const handleViewDetails = (template) => {
        setTemplateToPreview(template);
        setPreviewDialogOpen(true);
        handleMenuClose();
    };

    const handleDuplicateTemplate = async (template) => {
        handleMenuClose();

        try {
            const token = await getAccessTokenSilently();

            // Generate unique name with "Copy of" prefix
            let duplicateName = `Copy of ${template.name}`;
            let counter = 1;

            // Check if name exists and add counter if needed
            const existingNames = templates.map(t => t.name);
            while (existingNames.includes(duplicateName)) {
                counter++;
                duplicateName = `Copy of ${template.name} (${counter})`;
            }

            const templateData = {
                name: duplicateName,
                description: template.description || '',
                operationId: template.operationId,
                isShared: false // Duplicate as private by default
            };


            await createMissionTemplate(
                templateData,
                token,
                currentOrganization?.organizationUuid || null
            );

            // Refresh the list
            await loadTemplates();

            // Show success message (could add a toast/snackbar here)
        } catch (err) {
            if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else {
                setError('Failed to duplicate template. Please try again.');
            }
        }
    };

    const handleToggleSharing = async (template) => {
        try {
            const token = await getAccessTokenSilently();
            await updateMissionTemplate(
                template.id,
                { isShared: !template.isShared },
                token,
                currentOrganization?.organizationUuid || null
            );
            await loadTemplates();
            handleMenuClose();
        } catch (err) {
            setError('Failed to update template sharing');
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
            return;
        }

        try {
            const token = await getAccessTokenSilently();
            await deleteMissionTemplate(
                templateId,
                token,
                currentOrganization?.organizationUuid || null
            );
            await loadTemplates();
            handleMenuClose();
        } catch (err) {
            setError('Failed to delete template');
        }
    };

    const handleUseTemplate = (template) => {
        // Navigate to operations create page with template data
        navigate('/operations/create', {
            state: {
                templateId: template.id,
                templateGeometry: template.operationGeometry,
                templateData: {
                    operationType: template.operationType,
                    maxAltitude: template.maxAltitude,
                    radius: template.radius,
                    duration: template.duration,
                    timezone: template.timezone
                }
            }
        });
    };

    const handleMenuClick = (event, template) => {
        setAnchorEl(event.currentTarget);
        setSelectedTemplate(template);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTemplate(null);
    };

    const canEditTemplate = (template) => {
        // Handle null/undefined template
        if (!template) return false;

        // Owner/Manager can edit any template, pilots can only edit their own
        if (isOwnerOrManager) return true;

        // If template has no createdBy info, only owner/manager can edit
        if (!template.createdBy) return false;

        return template.createdBy.userId === user?.sub;
    };

    const canDeleteTemplate = (template) => {
        // Same as edit permissions
        return canEditTemplate(template);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        // Show relative time for recent templates
        if (diffInDays === 0) return 'Today';
        if (diffInDays === 1) return 'Yesterday';
        if (diffInDays < 7) return `${diffInDays} days ago`;

        // Show formatted date for older templates
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'flex-start',
                    gap: 2,
                    mb: 2
                }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            Operation Templates
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Browse and use pre-configured operation templates created from existing operations
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/missionlog')}
                        sx={{ minWidth: isMobile ? 'auto' : 160 }}
                        fullWidth={isMobile}
                        size={isMobile ? 'medium' : 'large'}
                    >
                        Go to Mission Log
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Info for different roles */}
                {isOwnerOrManager ? (
                    <Alert severity="info" sx={{ mb: 0 }} variant="outlined">
                        As {userRole}, you can see all templates created by team members.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 0 }} variant="outlined">
                        Create operation templates from your operations in the Mission Log page.
                        Toggle sharing to make templates available to your organization.
                    </Alert>
                )}
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3, width: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons={isMobile ? 'auto' : false}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: { xs: 1, sm: 2 },
                        flexShrink: 0
                    }}
                >
                    <Tab
                        sx={{ minWidth: { xs: 120, sm: 160 } }}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: isMobile ? '0.813rem' : '0.875rem' }}>All Templates</span>
                                <Chip label={templates.length} size="small" color="primary" />
                            </Box>
                        }
                    />
                    <Tab
                        sx={{ minWidth: { xs: 120, sm: 160 } }}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: isMobile ? '0.813rem' : '0.875rem' }}>My Templates</span>
                                <Chip label={templates.filter(t => t.createdBy?.userId === user?.sub).length} size="small" />
                            </Box>
                        }
                    />
                    <Tab
                        sx={{ minWidth: { xs: 120, sm: 160 } }}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: isMobile ? '0.813rem' : '0.875rem' }}>Shared</span>
                                <Chip label={templates.filter(t => t.isShared).length} size="small" color="success" />
                            </Box>
                        }
                    />
                </Tabs>

                {/* Templates Table */}
                {filteredTemplates.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 10, px: 3, width: '100%', minHeight: 400, flex: 1 }}>
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                bgcolor: 'action.hover',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}
                        >
                            <FlightIcon sx={{ fontSize: 50, color: 'text.disabled' }} />
                        </Box>
                        <Typography variant="h6" color="text.primary" gutterBottom fontWeight={500}>
                            {currentTab === 1 ? 'No templates created yet' :
                             currentTab === 2 ? 'No shared templates available' :
                             'No templates available'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                            {currentTab === 1
                                ? 'Create your first operation template from an existing operation in the Mission Log page. Templates help you quickly recreate operations with the same parameters.'
                                : currentTab === 2
                                ? 'No operation templates have been shared with the organization yet. Share your templates to make them available to your team.'
                                : 'Create operation templates from existing operations in the Mission Log page to quickly recreate operations with the same flight parameters.'
                            }
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/missionlog')}
                            size="large"
                        >
                            Go to Mission Log
                        </Button>
                    </Box>
                ) : isMobile ? (
                    // Mobile Card Layout
                    <Box sx={{ width: '100%', flex: 1, p: 2 }}>
                        <Stack spacing={2}>
                            {filteredTemplates.map((template) => {
                                const isOwnTemplate = template.createdBy?.userId === user?.sub;

                                return (
                                    <Card
                                        key={template.id}
                                        variant="outlined"
                                        sx={{
                                            '&:hover': {
                                                boxShadow: 2
                                            },
                                            transition: 'box-shadow 0.2s'
                                        }}
                                    >
                                        <CardContent sx={{ pb: 1 }}>
                                            {/* Template Name & Type */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight={600}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        color: 'primary.main',
                                                        flex: 1,
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                    onClick={() => handleViewDetails(template)}
                                                >
                                                    {template.name}
                                                </Typography>
                                                {(canEditTemplate(template) || canDeleteTemplate(template)) && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleMenuClick(e, template)}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            {/* Description */}
                                            {template.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'block',
                                                        mb: 1.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {template.description}
                                                </Typography>
                                            )}

                                            {/* Type & Visibility */}
                                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    icon={<FlightIcon fontSize="small" />}
                                                    label={template.operationType === 'part_107' ? 'Part 107' : '44809'}
                                                    size="small"
                                                    color={template.operationType === 'part_107' ? 'primary' : 'secondary'}
                                                    variant="outlined"
                                                />
                                                {template.isShared ? (
                                                    <Chip
                                                        icon={<ShareIcon fontSize="small" />}
                                                        label="Shared"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<LockIcon fontSize="small" />}
                                                        label="Private"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>

                                            {/* Flight Parameters */}
                                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                                {template.maxAltitude && (
                                                    <Chip
                                                        icon={<HeightIcon fontSize="small" />}
                                                        label={`${template.maxAltitude}ft`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {template.radius && (
                                                    <Chip
                                                        icon={<RadiusIcon fontSize="small" />}
                                                        label={`${template.radius}ft`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                                <Chip
                                                    icon={<ScheduleIcon fontSize="small" />}
                                                    label={`${template.duration} min`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>

                                            {/* Creator & Date */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                                        {template.createdBy?.name?.charAt(0) || 'U'}
                                                    </Avatar>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {isOwnTemplate ? 'You' : template.createdBy?.name || 'Unknown'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(template.createdAt)}
                                                </Typography>
                                            </Box>
                                        </CardContent>

                                        <CardActions sx={{ pt: 0, px: 2, pb: 2, gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleViewDetails(template)}
                                                startIcon={<VisibilityIcon fontSize="small" />}
                                                sx={{ flex: 1, fontSize: '0.75rem' }}
                                            >
                                                Preview
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => handleUseTemplate(template)}
                                                startIcon={<FlightIcon fontSize="small" />}
                                                sx={{ flex: 1, fontSize: '0.75rem' }}
                                            >
                                                Use
                                            </Button>
                                        </CardActions>
                                    </Card>
                                );
                            })}
                        </Stack>
                    </Box>
                ) : (
                    // Desktop Table Layout
                    <Box sx={{ width: '100%', flex: 1 }}>
                        <TableContainer sx={{ width: '100%' }}>
                        <Table sx={{ width: '100%' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Template Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Flight Parameters</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Duration</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Creator</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Visibility</TableCell>
                                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Created</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTemplates.map((template) => {
                                    const isOwnTemplate = template.createdBy?.userId === user?.sub;

                                    return (
                                        <TableRow
                                            key={template.id}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                                },
                                                transition: 'all 0.2s',
                                                '&:last-child td': { borderBottom: 0 }
                                            }}
                                        >
                                            {/* Template Name & Description */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight={500}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                    onClick={() => handleViewDetails(template)}
                                                >
                                                    {template.name}
                                                </Typography>
                                                {template.description && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'block',
                                                            mt: 0.5,
                                                            maxWidth: 300,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {template.description}
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            {/* Operation Type */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Chip
                                                    icon={<FlightIcon fontSize="small" />}
                                                    label={template.operationType === 'part_107' ? 'Part 107' : '44809'}
                                                    size="small"
                                                    color={template.operationType === 'part_107' ? 'primary' : 'secondary'}
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            {/* Flight Parameters */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Stack direction="row" spacing={1}>
                                                    {template.maxAltitude && (
                                                        <Tooltip title="Max Altitude">
                                                            <Chip
                                                                icon={<HeightIcon fontSize="small" />}
                                                                label={`${template.maxAltitude}ft`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {template.radius && (
                                                        <Tooltip title="Radius">
                                                            <Chip
                                                                icon={<RadiusIcon fontSize="small" />}
                                                                label={`${template.radius}ft`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>

                                            {/* Duration */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Chip
                                                    icon={<ScheduleIcon fontSize="small" />}
                                                    label={`${template.duration} min`}
                                                    size="small"
                                                />
                                            </TableCell>

                                            {/* Creator */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem' }}>
                                                        {template.createdBy?.name?.charAt(0) || 'U'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {isOwnTemplate ? 'You' : template.createdBy?.name || 'Unknown'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* Visibility */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                {template.isShared ? (
                                                    <Chip
                                                        icon={<ShareIcon fontSize="small" />}
                                                        label="Shared"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<LockIcon fontSize="small" />}
                                                        label="Private"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Created Date */}
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(template.createdAt)}
                                                </Typography>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="right" sx={{ py: 2.5 }}>
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleViewDetails(template)}
                                                        startIcon={<VisibilityIcon />}
                                                    >
                                                        Preview
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleUseTemplate(template)}
                                                        startIcon={<FlightIcon />}
                                                    >
                                                        Use
                                                    </Button>
                                                    {(canEditTemplate(template) || canDeleteTemplate(template)) && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleMenuClick(e, template)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>
                )}
            </Paper>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canEditTemplate(selectedTemplate) && (
                    <MenuItem onClick={() => handleEditTemplate(selectedTemplate)}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        Edit Template
                    </MenuItem>
                )}
                <MenuItem onClick={() => handleDuplicateTemplate(selectedTemplate)}>
                    <CopyIcon fontSize="small" sx={{ mr: 1 }} />
                    Duplicate Template
                </MenuItem>
                {canEditTemplate(selectedTemplate) && selectedTemplate?.createdBy?.userId === user?.sub && (
                    <MenuItem onClick={() => handleToggleSharing(selectedTemplate)}>
                        {selectedTemplate?.isShared ? (
                            <>
                                <LockIcon fontSize="small" sx={{ mr: 1 }} />
                                Make Private
                            </>
                        ) : (
                            <>
                                <ShareIcon fontSize="small" sx={{ mr: 1 }} />
                                Share with Organization
                            </>
                        )}
                    </MenuItem>
                )}
                {canDeleteTemplate(selectedTemplate) && (
                    <MenuItem onClick={() => handleDeleteTemplate(selectedTemplate?.id)}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        Delete
                    </MenuItem>
                )}
            </Menu>

            {/* Edit Template Dialog */}
            <EditTemplateDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setTemplateToEdit(null);
                }}
                template={templateToEdit}
                onSuccess={handleTemplateUpdated}
            />

            {/* Preview Template Dialog */}
            <TemplatePreviewDialog
                open={previewDialogOpen}
                onClose={() => {
                    setPreviewDialogOpen(false);
                    setTemplateToPreview(null);
                }}
                template={templateToPreview}
                onUse={handleUseTemplate}
            />
        </Box>
    );
};

export default MissionTemplates;
